import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import api from '../services/api'
import CarteProjet from '../components/CarteProjet'
import CarteSeminaire from '../components/CarteSeminaire'
import { Search, Map, Loader2, Briefcase, ChevronLeft, ChevronRight } from 'lucide-react'

const Projets = () => {
  const [region, setRegion] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const { hash } = useLocation()
  
  // Pagination states
  const [ongoingPage, setOngoingPage] = useState(1)
  const [archivePage, setArchivePage] = useState(1)
  const [seminairesPage, setSeminairesPage] = useState(1)
  const limit = 10

  // Handle search debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
      setOngoingPage(1)
      setArchivePage(1)
      setSeminairesPage(1)
    }, 500)
    return () => clearTimeout(timer)
  }, [searchTerm])

  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '')
      const element = document.getElementById(id)
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' })
        }, 100)
      }
    }
  }, [hash])

  const { data: ongoingData, isLoading: ongoingLoading, isError: ongoingError } = useQuery({
    queryKey: ['projets-en-cours', region, debouncedSearch, ongoingPage],
    queryFn: async () => {
      const params = { 
        skip: (ongoingPage - 1) * limit, 
        limit, 
        statut: 'en_cours',
        q: debouncedSearch 
      }
      if (region) params.region = region
      const res = await api.get('/projets/', { params })
      return res.data
    }
  })

  const { data: archiveData, isLoading: archiveLoading, isError: archiveError } = useQuery({
    queryKey: ['projets-archive', region, debouncedSearch, archivePage],
    queryFn: async () => {
      const params = { 
        skip: (archivePage - 1) * limit, 
        limit, 
        statut: 'archive',
        q: debouncedSearch 
      }
      if (region) params.region = region
      const res = await api.get('/projets/', { params })
      return res.data
    }
  })

  const { data: seminairesData, isLoading: seminairesLoading, isError: seminairesError } = useQuery({
    queryKey: ['seminaires', debouncedSearch, seminairesPage],
    queryFn: async () => {
      const params = { 
        skip: (seminairesPage - 1) * limit, 
        limit,
        q: debouncedSearch
      }
      const res = await api.get('/seminaires/', { params })
      return res.data
    }
  })

  const regions = ["Abidjan", "Boundiali", "Man", "San Pedro", "Bouaké", "Korhogo"]

  const isLoading = ongoingLoading || archiveLoading || seminairesLoading
  const isError = ongoingError || archiveError || seminairesError

  const Pagination = ({ current, total, onPageChange }) => {
    const totalPages = Math.ceil(total / limit)
    if (totalPages <= 1) return null

    return (
      <div className="flex justify-center items-center gap-4 mt-8">
        <button 
          onClick={() => onPageChange(Math.max(1, current - 1))}
          disabled={current === 1}
          className="p-2 rounded-full border disabled:opacity-30 hover:bg-gray-100 transition-colors"
        >
          <ChevronLeft size={20} />
        </button>
        <div className="flex gap-2">
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i + 1}
              onClick={() => onPageChange(i + 1)}
              className={`w-8 h-8 rounded-md flex items-center justify-center text-sm font-medium transition-colors ${
                current === i + 1 
                  ? 'bg-cidh-blue text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-100 border'
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>
        <button 
          onClick={() => onPageChange(Math.min(totalPages, current + 1))}
          disabled={current === totalPages}
          className="p-2 rounded-full border disabled:opacity-30 hover:bg-gray-100 transition-colors"
        >
          <ChevronRight size={20} />
        </button>
      </div>
    )
  }

  return (
    <div className="pt-24 pb-16">
      {/* Banner */}
      <div 
        className="text-white py-20 mb-12 relative overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: 'url("/images/formation.jpeg")' }}
      >
        <div className="absolute inset-0 bg-cidh-green/80"></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="flex flex-col items-center text-center">
            <div className="bg-cidh-gold p-3 rounded-2xl mb-6">
              <Briefcase size={40} className="text-cidh-blue" />
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold font-montserrat mb-6 tracking-tight">
              Nos Projets de Développement
            </h1>
            <p className="text-gray-100 max-w-2xl text-lg leading-relaxed">
              Impact concret sur le terrain à travers nos interventions multisectorielles.
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row gap-6 mb-12 bg-cidh-gray-light p-6 rounded-lg shadow-sm">
          <div className="flex-grow flex items-center bg-white border rounded-md px-3">
            <Search className="text-gray-400" size={20} />
            <input 
              type="text" 
              placeholder="Rechercher par mot-clé..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-2 outline-none"
            />
          </div>
          <div className="flex items-center gap-4">
            <Map className="text-cidh-blue" size={20} />
            <select 
              className="p-2 border rounded-md bg-white outline-none"
              value={region}
              onChange={(e) => {
                setRegion(e.target.value)
                setOngoingPage(1)
                setArchivePage(1)
              }}
            >
              <option value="">Toutes les régions</option>
              {regions.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="animate-spin text-cidh-blue" size={48} />
          </div>
        ) : isError ? (
          <div className="text-center py-24 text-red-500">
            Une erreur est survenue lors du chargement des projets. Veuillez réessayer plus tard.
          </div>
        ) : (
          <div className="space-y-16">
            <section id="en-cours">
              <div className="flex items-center gap-4 mb-8">
                <h2 className="text-2xl font-bold text-cidh-blue font-montserrat">Projets en cours ({ongoingData?.total || 0})</h2>
                <div className="h-1 flex-grow bg-cidh-gold/20 rounded-full"></div>
              </div>
              
              {!ongoingData?.items || ongoingData.items.length === 0 ? (
                <div className="text-center py-12 text-gray-400 bg-gray-50 rounded-lg border-2 border-dashed">
                  Aucun projet en cours trouvé.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {ongoingData.items.map(projet => (
                      <CarteProjet key={projet.id} projet={projet} />
                    ))}
                  </div>
                  <Pagination 
                    current={ongoingPage} 
                    total={ongoingData.total} 
                    onPageChange={setOngoingPage} 
                  />
                </>
              )}
            </section>

            <section id="archive">
              <div className="flex items-center gap-4 mb-8">
                <h2 className="text-2xl font-bold text-cidh-gray-text font-montserrat">Archives Projets ({archiveData?.total || 0})</h2>
                <div className="h-1 flex-grow bg-gray-200 rounded-full"></div>
              </div>
              
              {!archiveData?.items || archiveData.items.length === 0 ? (
                <div className="text-center py-12 text-gray-400 bg-gray-50 rounded-lg border-2 border-dashed">
                  Aucun projet archivé trouvé.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {archiveData.items.map(projet => (
                      <CarteProjet key={projet.id} projet={projet} />
                    ))}
                  </div>
                  <Pagination 
                    current={archivePage} 
                    total={archiveData.total} 
                    onPageChange={setArchivePage} 
                  />
                </>
              )}
            </section>

            <section id="seminaires">
              <div className="flex items-center gap-4 mb-8">
                <h2 className="text-2xl font-bold text-cidh-blue font-montserrat">Formation professionnelle continue et séminaires réalisés ({seminairesData?.total || 0})</h2>
                <div className="h-1 flex-grow bg-cidh-gold/20 rounded-full"></div>
              </div>
              
              {!seminairesData?.items || seminairesData.items.length === 0 ? (
                <div className="text-center py-12 text-gray-400 bg-gray-50 rounded-lg border-2 border-dashed">
                  Aucun séminaire trouvé.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {seminairesData.items.map(seminaire => (
                      <CarteSeminaire key={seminaire.id} seminaire={seminaire} />
                    ))}
                  </div>
                  <Pagination 
                    current={seminairesPage} 
                    total={seminairesData.total} 
                    onPageChange={setSeminairesPage} 
                  />
                </>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  )
}

export default Projets
